import React, { useCallback, useEffect, useMemo } from 'react';
import Slider from 'react-rangeslider';
import 'react-rangeslider/lib/index.css';
import clsx from 'clsx';
import Loading from '../Shared/Loader';
import './index.scss';
import If from '../If';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getBestSellerProduct } from '../../../redux/actions/customizer';
import VideoWithFallback from '../VideoWithFallback';
import { getFontOptions, getSelectedOption } from '../../../utils/commonRingUtils';
import EngravingFilter from '../EngravingFilter/EngravingFilter';
import { APP_STYLES } from '../../../utils/basic';
import { useRingCustomizerContext } from '../../context/RingCustomizerContext';
import Accordion from '../Accordion';
import AppModal from '../AppModal';
import { toast } from 'react-toastify';
import LoaderIcon from '../../../assets/images/loader2inv.gif';

type IProps = {
  id: string;
  handleBestSeller?: (id: string) => void;
  engraving: string;
  setEngraving: (text: string) => void;
  size: number;
  setSize: (size: number) => void;
  handleBestSellerDone: () => void;
  isBestSellerLoading: boolean;
  product: any;
  navigateToUrl: (url: string, replace?: boolean, bestSeller?: string) => void;
  isOpen: boolean;
  onClose: (ringUrl?: string) => void;
};


function DiscountedPopupLoadingPlaceholder() {
  return (
    <div
      className="tw-w-full tw-flex tw-items-center tw-justify-center tw-min-h-[300px]"
      aria-hidden
    >
      <img src={LoaderIcon} alt="Loading" className="tw-opacity-50" />
    </div>
  );
}

let timedOutId: NodeJS.Timeout | undefined;

const DiscountedPopup = ({
  id,
  handleBestSeller,
  product,
  navigateToUrl,
  engraving,
  setEngraving,
  size,
  setSize,
  handleBestSellerDone,
  isBestSellerLoading,
  isOpen,
  onClose
}: IProps) => {
  const { subpath, storeData } = useRingCustomizerContext();
  const { bestSellerProduct, isFetching } = useAppSelector((state) => state.customizer);
  const dispatch = useAppDispatch();

  const fetchBestSellerProduct = useCallback(
    async (data?: { size: number; engraving: string }) => {
      const { size, engraving } = data || {};
      try {
        const payload = {
          id,
          data: {
            size: size || '',
            engraving: engraving || '',
            subpath: subpath,
            currencyCode: storeData?.currency || 'USD'
          }
        };
        await dispatch(getBestSellerProduct(payload)).unwrap();
      } catch (error: any) {
        console.log(error);
        toast.error(error?.response?.data?.message || 'Unable to fetch best seller product', {autoClose: 5000});
        onClose();
      }
    },
    [dispatch, id, storeData?.currency, subpath]
  );

  useEffect(() => {
    if (id && (!bestSellerProduct || bestSellerProduct?.bestseller_id !== id)) {
      fetchBestSellerProduct();
    }
  }, [bestSellerProduct, fetchBestSellerProduct, id]);

  const handleClickSimilarRing = (ring: any) => {
    onClose(ring?.product_url);
  };

  const handleClose = () => {
    onClose();
  };

  const handleClick = (id: string) => () => {
    navigateToUrl(bestSellerProduct?.ring_options_url, false, id);
  };

  const handleOnChange = (updated: any) => {
    if (updated.engraving_new?.includes('None')) {
      setEngraving('');
      fetchBestSellerProduct({ size: size, engraving: '' });
    } else {
      setEngraving(updated.engraving_new);
      fetchBestSellerProduct({ size: size, engraving: updated.engraving_new });
    }
  };

  const getMinRange = (list: any[]) => (list && list.length > 0 ? parseFloat(list[0].sku) : 3);

  const getMaxRange = (list: any[]) => (list && list.length > 0 ? parseFloat(list[list.length - 1].sku) : 24);

  const getStep = (list: any[]) => (list && list.length > 0 ? parseFloat(list[1].sku) - parseFloat(list[0].sku) : 0.25);

  const handleSliderChange = (value: number) => {
    setSize(value);
    if (timedOutId) {
      clearTimeout(timedOutId);
    }
    timedOutId = setTimeout(() => {
      fetchBestSellerProduct({ size: value, engraving: engraving });
    }, 1000);
  };

  const getEngravingContent = () => {
    const content = {
      hasSwatch: false,
      fieldType: 'engraving_new',
      options: getFontOptions(
        bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []
      ),
      selectedOption: getSelectedOption(
        bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []
      ),
      engravingText: bestSellerProduct?.engraving_new?.text?.text || ''
    };
    return (
      <div className="tw-w-full tw-h-full tw-bg-blue-50">
        <EngravingFilter onChange={handleOnChange} isFetching={false} content={content} />
      </div>
    );
  };

  const canShowSimilarRings = useMemo(() => {
    return bestSellerProduct?.show_similar_rings && bestSellerProduct?.similar_rings?.length > 0;
  }, [bestSellerProduct]);

  const similar_rings = useMemo(() => {
    if (!canShowSimilarRings) return [];
    const array = [{
        highlights: bestSellerProduct?.data?.highlights,
        price: bestSellerProduct?.price,
        image_url: `${process.env.REACT_APP_ASSET_URL}${bestSellerProduct?.images?.[0]}`,
        product_url: bestSellerProduct?.ring_options_url,
        title: bestSellerProduct?.title || bestSellerProduct?.description,
      },
      ...(bestSellerProduct?.similar_rings ?? [])
    ]
    const priceOf = (ring: any) => parseFloat(String(ring?.price ?? '').replace(/[$,]/g, '')) || 0;
    return array.sort((a, b) => priceOf(a) - priceOf(b));
  }, [canShowSimilarRings, bestSellerProduct]);

  const showLoadingPlaceholder = Boolean(
    isOpen &&
      id &&
      (!bestSellerProduct ||
        String(bestSellerProduct?.bestseller_id ?? '') !== String(id)),
  );

  const showMagicNumberAtDiscountPopup = APP_STYLES[subpath]?.showMagicNumberAtDiscountPopup;
  const isEngravingEnabled = bestSellerProduct?.is_engraving_enabled;
  return (
    <AppModal size="xxl" isOpen={isOpen} onClose={handleClose} adjustToContent={canShowSimilarRings || showLoadingPlaceholder}>
      <div className="-tw-my-5 tw-p-5 xl:tw-p-8 tw-relative tw-flex tw-flex-col tw-min-h-[300px] tw-max-h-[90vh]">
      <If condition={Boolean(canShowSimilarRings && !!bestSellerProduct && !showLoadingPlaceholder)}>
        <div className="tw-w-full">
          <h4 className="tw-font-reckless tw-text-4xl tw-font-normal tw-text-primary-color tw-mb-6">
            Select a Starting Point
          </h4>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-2 tw-gap-4">
            {similar_rings?.map((ring: any) => (
              <button
                key={ring?.product_url}
                type="button"
                onClick={() => handleClickSimilarRing(ring)}
                className="similar-ring-card"
              >
                <div className="tw-h-[135px] tw-flex tw-items-center tw-justify-center tw-shrink-0">
                  <img
                    src={ring?.image_url}
                    alt="Similar ring"
                    className="tw-max-w-full tw-max-h-full tw-object-contain"
                  />
                </div>
                <div className="tw-flex tw-flex-col tw-items-start tw-text-left tw-gap-5">
                  <div className="tw-text-xl tw-font-semibold tw-leading-none tw-text-black">
                    {(() => {
                      const [dollars, cents] = String(ring?.price ?? '').split('.');
                      return (
                        <>
                          {dollars}
                          {cents !== undefined && (
                            <sup className="tw-text-xs tw-font-semibold tw-ml-0.5">.{cents}</sup>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className="tw-flex tw-flex-col tw-gap-1 tw-flex-1">
                    {ring?.highlights?.map((highlight: any) => (
                      <div className="tw-text-black tw-text-sm tw-leading-tight">
                        {highlight}
                      </div>
                    ))}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </If>
      <If condition={Boolean(!canShowSimilarRings && !!bestSellerProduct && !showLoadingPlaceholder)}>
        <div className="tw-flex tw-flex-wrap tw-gap-4 xl:tw-gap-8">
          <div className="tw-w-full lg:tw-flex-1 shadowBox">
            <div className="tw-px-5">
              <h4 className="tw-font-reckless tw-text-center tw-text-xl tw-font-bold">Ready to Ship</h4>
              <div className="tw-flex tw-justify-center tw-my-3">
                <img
                  src={`${process.env.REACT_APP_ASSET_URL}${bestSellerProduct?.images[0]}`}
                  alt="ring"
                  className="tw-h-52 tw-object-cover"
                />
              </div>
              <If condition={bestSellerProduct?.variants?.length > 1}>
                <div className="tw-flex tw-flex-col sm:tw-flex-row  tw-flex-wrap tw-justify-center tw-gap-3 tw-mb-8">
                  {bestSellerProduct?.variants?.map((variant: any) => (
                    <button
                      key={variant.bestseller_id}
                      onClick={!variant.selected ? handleClick(variant.bestseller_id) : undefined}
                      className={clsx('tw-text-sm', {
                        'solid-border': !variant.selected,
                        'dotted-border': variant.selected
                      })}
                    >
                      {variant.name}
                    </button>
                  ))}
                </div>
              </If>
              <div className="tw-text-primary-color">{bestSellerProduct?.description}</div>
              <div className="tw-my-5">
                <span className="tw-text-brand-color tw-line-through">{bestSellerProduct?.msrp}</span>
                <span className="tw-text-primary-color tw-pl-2">{bestSellerProduct?.price}</span>
                <span className="tw-text-primary-color tw-italic tw-pl-2">in stock discount</span>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-2">
                <div className="tw-text-primary-color tw-text-base tw-font-bold">
                  Estimate your Size : <b>{size}</b>
                </div>
                <Slider
                  min={getMinRange(bestSellerProduct?.finger_size || bestSellerProduct?.size)}
                  max={getMaxRange(bestSellerProduct?.finger_size || bestSellerProduct?.size)}
                  step={getStep(bestSellerProduct?.finger_size || bestSellerProduct?.size)}
                  value={size}
                  tooltip={true}
                  onChange={handleSliderChange}
                />
              </div>
              <If condition={(bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []).length > 0}>
                <div className="tw-flex tw-flex-col tw-gap-2 -tw-mx-4">
                  <If condition={Boolean(!isEngravingEnabled)}>
                    <Accordion
                      title="Choose Engraving :"
                      isJXSContent
                      content={getEngravingContent()}

                    />
                  </If>
                </div>
              </If>
            </div>
            <div className="tw-mt-5">
              <If condition={showMagicNumberAtDiscountPopup}>
                <button
                  className="hover:tw-bg-brand-color hover:tw-text-tertiary-color tw-block tw-bg-brand-color tw-font-medium tw-px-9 tw-py-2 tw-rounded-tertiary tw-text-tertiary-color tw-w-full tw-text-center"
                >
                  Magic Number: {bestSellerProduct?.magic_number}
                </button>
              </If>
              <If condition={!showMagicNumberAtDiscountPopup}>
                <button
                  className="hover:tw-bg-brand-color hover:tw-text-tertiary-color tw-block tw-bg-brand-color tw-font-medium tw-px-9 tw-py-2 tw-rounded-tertiary tw-text-tertiary-color tw-w-full tw-text-center"
                  onClick={handleBestSellerDone}
                >
                  Proceed As Is
                </button>
              </If>
            </div>
          </div>
          <div className="tw-w-full lg:tw-flex-1 shadowBox">
            <h4 className="tw-font-reckless tw-text-center tw-text-xl tw-font-bold">Make it Your Own</h4>
            <div className="tw-flex-1 tw-my-3">
              <VideoWithFallback videoUrl={bestSellerProduct?.video_url} className="discounted-video-box" />
            </div>
            <div className="tw-mt-5">
              <button
                // to={`/shop${bestSellerProduct?.ring_options_url}`}
                // target="_blank"
                onClick={handleClose}
                className="hover:tw-bg-brand-color hover:tw-text-tertiary-color tw-block tw-bg-brand-color tw-font-medium tw-px-9 tw-py-2 tw-rounded-tertiary tw-text-tertiary-color tw-w-full tw-text-center"
              >
                Customize in Lashbrook Builder
              </button>
            </div>
          </div>
        </div>
        {/* <div className="tw-py-6">
          <p className="tw-text-center tw-text-lg tw-font-bold tw-mb-4">
            Other popular combinations
          </p>
          <div className="tw-flex tw-gap-14 tw-overflow-x-hidden tw-px-2 tw-justify-center tw-overflow-hidden">
            {bestSellerProduct?.similar_rings?.map((item: any) => (
              <button
                key={item.ring_options_url} // use unique string from url or item.title if you want
                type="button"
                className="tw-flex-[0_0_160px] tw-text-center tw-cursor-pointer"
                // onClick={() =>
                //   navigateToUrl(item?.ring_options_url || bestSellerProduct?.ring_options_url, false, item.ring_options_url)
                // }
                // title={item.title}
              >
                <div className="tw-w-full tw-h-[140px] tw-flex tw-items-center tw-justify-center">
                  <img src={`${item.image_url}`} alt={item.title || "ring"} className="tw-object-contain tw-h-full" />
                </div>
                <div className="tw-mt-2 tw-text-sm tw-font-normal tw-font-[18px]"> {item.price} </div>
              </button>
            ))}
          </div>
        </div> */}
      </If>
      <If condition={showLoadingPlaceholder}>
        <DiscountedPopupLoadingPlaceholder />
      </If>
      </div>
    </AppModal>
  );
};

export default DiscountedPopup;
